/* global PowerPoint */
import { applyModifications, listSlides } from "./services/slideService";
import { guardedPowerPointRun } from "./services/officeJsGuard";
import { shouldUseFactSetBridge } from "./services/presentationHost";
import {
  createBlankSlideAtEndViaIntents,
  deleteSlidesViaIntents,
  type FactSetPresentationRef,
  resolveSlideIndexByIdViaIntents,
} from "./services/factset/intentBridgeService";
import { PresentationModification } from "./types";
import { DeckPlan } from "./services/ai/deckPlannerService";

export async function resolveCurrentIndexForSlideId(
  slideId: string,
  log?: (message: string) => void,
  options?: { presentationRef?: FactSetPresentationRef | null }
): Promise<number | null> {
  // FactSet fast path: resolve index from slide-list cache.
  if (shouldUseFactSetBridge()) {
    try {
      return await resolveSlideIndexByIdViaIntents(slideId, log, {
        presentationRef: options?.presentationRef,
      });
    } catch (e) {
      console.warn(
        `[FactSet] resolveCurrentIndexForSlideId via slide list failed, falling back: ${e instanceof Error ? e.message : String(e)}`
      );
    }
  }
  // Fallback path: only needs id → index. listSlides() is cheap (uses cached
  // GetSlideList via the FactSet bridge or a single Office.JS sync otherwise)
  // versus the full RunOfficeExtractor trip the consumer used to issue.
  const refreshed = await listSlides({ presentationRef: options?.presentationRef });
  const match = refreshed.find((slide) => slide.id === slideId);
  return match ? match.index : null;
}

export async function createBlankSlideAtEnd(
  blankLayoutId?: string,
  options?: { presentationRef?: FactSetPresentationRef | null }
): Promise<string | null> {
  if (shouldUseFactSetBridge()) {
    try {
      return await createBlankSlideAtEndViaIntents(undefined, {
        presentationRef: options?.presentationRef,
      });
    } catch (e) {
      console.warn(
        `[FactSet] createBlankSlideAtEnd via intents failed: ${e instanceof Error ? e.message : String(e)}`
      );
      return null;
    }
  }

  return guardedPowerPointRun(async (context) => {
    const slidesBefore = context.presentation.slides;
    slidesBefore.load("items/id");
    await context.sync();
    const beforeIds = new Set(slidesBefore.items.map((slide) => slide.id));

    const addOptions: Record<string, unknown> = { index: slidesBefore.items.length };
    if (blankLayoutId) {
      addOptions.slideMasterLayoutId = blankLayoutId;
    }
    (slidesBefore as any).add(addOptions);
    await context.sync();

    const slidesAfter = context.presentation.slides;
    slidesAfter.load("items/id");
    await context.sync();
    const inserted = slidesAfter.items.find((slide) => !beforeIds.has(slide.id));
    return inserted?.id || null;
  }, null);
}

export async function deleteSlideById(
  slideId: string,
  options?: { presentationRef?: FactSetPresentationRef | null }
): Promise<boolean> {
  if (shouldUseFactSetBridge()) {
    try {
      const deleted = await deleteSlidesViaIntents([slideId], {
        presentationRef: options?.presentationRef,
      });
      if (!deleted) {
        return false;
      }
      // Some host builds can accept DeleteSlides without error but no-op when
      // the target presentation isn't active. Verify the id is actually gone.
      const remainingSlides = await listSlides({
        forceRefresh: true,
        presentationRef: options?.presentationRef,
      });
      return !remainingSlides.some((slide) => slide.id === slideId);
    } catch (e) {
      console.warn(
        `[FactSet] deleteSlideById via DeleteSlides intent failed: ${e instanceof Error ? e.message : String(e)}`
      );
      return false;
    }
  }

  return guardedPowerPointRun(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");
    await context.sync();
    const target = slides.items.find((slide) => slide.id === slideId);
    if (!target) return false;
    target.delete();
    await context.sync();
    return true;
  }, false);
}

export async function applyFallbackContentToPlanSlide(
  planIndex: number,
  reason: string,
  plan: DeckPlan,
  planIndexToSlideId: Map<number, string>,
  log: (message: string) => void,
  options?: {
    presentationRef?: FactSetPresentationRef | null;
    preferredBounds?: { slideWidth: number; slideHeight: number };
  }
): Promise<boolean> {
  const slideId = planIndexToSlideId.get(planIndex);
  if (!slideId) return false;
  const slideIndex = await resolveCurrentIndexForSlideId(slideId, log, {
    presentationRef: options?.presentationRef,
  });
  if (slideIndex === null) return false;

  const outline = plan.slides[planIndex];
  const fallbackModifications: PresentationModification = {
    slides: [
      {
        id: slideId,
        index: slideIndex,
        action: "modify",
        shapes: [
          {
            id: `fallback_title_${planIndex}`,
            action: "create",
            text: outline?.title || `Slide ${planIndex + 1}`,
            left: 40,
            top: 24,
            width: 880,
            height: 60,
            fontSize: 34,
            bold: true,
            textColor: "#0F3634",
          },
          {
            id: `fallback_body_${planIndex}`,
            action: "create",
            text: `Content generation was partially unavailable for this slide.\n\nReason: ${reason.slice(0, 220)}`,
            left: 56,
            top: 120,
            width: 848,
            height: 220,
            fontSize: 20,
            textColor: "#5A7A77",
          },
        ],
      },
    ],
  };

  await applyModifications(fallbackModifications, log, undefined, {
    presentationRef: options?.presentationRef,
    preferredBounds: options?.preferredBounds,
  });
  return true;
}
